const patchNotes = `

## Version 1.7.0 (Android and Windows)

### New Features
- Real Time Inspector - renamed from Button Overview and added new filter options
- Record button activity to track trigger counts and timestamps (available in Real Time Inspector)
- Deck Inspector - Load and analyze your deck JSON files offline, filter buttons by commands (Windows only)
- Automatically launch Deck Hopper Server when Windows starts (enable in Settings)

### Improvements
- Connection button now shows "Connecting..." state
- Strict Image Checking mode - images are now requested on each load (enable in Settings)
- Button text scaling - no longer recalculates on minor size changes

### Bug Fixes
- Error popups when no internet connection is available
- Potential crash when there are no decks available to display
- Scrolling issues in menus when ad banner is displayed (Android)

## Version 1.6.1 (Android)

## Improvements
- Further fixes after updating Google Play Billing Library version to comply with Google Play requirements

## Version 1.6.0 (Android and Windows)

### New Features
- Browser and OBS Dock Support (Windows)
  - Open Deck Hopper Server to run Deck Hopper in a browser or OBS Dock.
  - [How To Guide](https://christinak.ca/deckhopper/setup#deckHopperServer)
- New Settings 'Lock Screen Orientation' (Android)
    - Force and lock Deck Hopper's orientation. This overrides system rotation.
- 'Check Connection' command in Deck Hopper Advanced
    - Ping Deck Hopper to see what port it’s running on and which SAMMI it’s connected to.

### Improvements
- 'Wait for Multi Choice' popup updated with a custom scrollbar and more readable text.
- Updated Android target API level and Google Play Billing Library to meet Google Play Store requirements. (Android)


## Version 1.5.0

### New Features
- New Deck Hopper Advanced commands:
  - Popup Message: Displays a popup message within Deck Hopper.
  - Close Popup Message: Closes a popup message within Deck Hopper.
  - Open URL: Opens a URL within Deck Hopper.
  - Close URL: Closes a URL within Deck Hopper.

### Improvements
- More efficient image caching reduces packets sent to SAMMI (requires SAMMI 2025.1.1 or later).
- Removed Google Ads and replaced them with a less intrusive custom ad banner (Android).
- Reworked app icon
- Minor UI enhancements to the Account Tab.
- Minor text improvements.
- Updates to the licensing model (free access remains available for now)

### Bug Fixes
- Fixed the auto-connect flag occasionally failing (Desktop).

## Version 1.4.0

### New Features

- Added **Overview** to view all buttons and filter them by deck, trigger, etc. Works for encrypted decks as well! *(Pro feature)*  
- Added a navigation bar. If you do not want it to appear while inside a deck, you can disable it in Settings.  
- Added a **Status** button that displays the Deck Hopper Advanced extension status and SAMMI connection logs (when logging is enabled).  
- Added an **X** button and a menu option to exit full screen on Desktop. Right-click anywhere on the screen to access the menu. Only works when full screen is automatically enabled (not when manually toggled using F11).  
- Added an **Exit Deck** option to the button menu when in the Deck Area to return to the deck list.  
- Added an option in Settings to disable the context menu completely (useful for Windows touchscreen devices).  

### Improvements

- Completely reworked the UI to look more modern and user-friendly.  
- You can now expand each Settings option to see more details.  
- Improved the tutorial system, allowing you to bring it up at any time from the Menu under the Help tab.  
- Removed the persistent footer.
- Simplified the button menu and added submenus to avoid excessive length.  
- Optimized image loading and caching.  
- The navbar now automatically adjusts to vertical when on mobile in landscape mode, giving you more space in the deck area.  
- Added another bunny to welcome you on the connect screen. His name is Rudolf.  
- Improved the login screen, including an option to show/hide the password.  
- Made other text and visual improvements.  

### Bug Fixes

- Fixed button text not resizing correctly when Deck Hopper is minimized.  
- Fixed proxy buttons not working properly in some cases.  
- Deck thumbnails now correctly show the deck background color.  
- Fixed Deck Hopper Advanced version detection issues.  
- Removed an annoying quick 'blink' outline when switching between decks.  
- Fixed a white screen appearing between the splash screen and the app.  
- Other minor fixes.

## Version 1.3.2

### Bug Fixes
- Fixed an update token expiration issue

## Version 1.3.1

###  New Features 
- Added option to hide decks in the deck list (Pro)  
    - The decks are still active, however they will not be displayed in the deck list and when cycling through decks, they will be skipped.
    - You can toggle 'Show Hidden Decks' in the menu to show/hide hidden decks.
- Added proxy buttons (Pro)
    - You can now create button shortcuts, allowing you to place them anywhere in your SAMMI
    - Proxy buttons inherit all the properties of the button they are pointing to
    - This way, you can create a deck of your favorite buttons without needing to move them out of their original deck
    - Use Deck Hopper Advanced extension to create proxy buttons

### Improvements 
- Hazel, Archie or Skye will now randomly greet you on the Connection tab
- Added a prompt to review the app on the Play Store after a certain number of uses
- Various code optimizations, typo fixes, etc.

### Bug Fixes
- Fixed an issue with submitting bug reports
- Resolved inconsistencies with the launch flags
- Resolved a connect button bug in 1.3.0


## Version 1.2.3

### New Features 
- Twitch Embed buttons: You can now add a Twitch video or chat as a button in your deck. It supports login as well. Use the Deck Hopper Advanced extension to create one. (Pro)
- Expanded Deck Hopper launch options: You can now specify connection details, fullscreen mode, always on top, and more when launching Deck Hopper (Pro Desktop).

### Improvements 
- You can dismiss 'Deck App: Wait for' dialogs by pressing Enter (Desktop).
- You can now use the scroll wheel to cycle between decks (Desktop).
- Added caching to button text sizes to improve performance when switching between decks.
- 'Settings' renamed to 'Menu': To avoid confusion with another 'Settings' tab inside the menu.
- Improved tutorial wording.
- Replaced our generic bunny icon in the menu with Hazel to honor her after she passed away.
- Additional performance optimizations.

### Bug Fixes
- Fixed a bug with button text sizes not resizing correctly after the app was minimized.
- Fixed a bug with active buttons sometimes displaying incorrect or no animation after minimizing the app or switching between decks.
- Fixed the issue where you could only select 'Connection Issue' as the issue type when reporting bugs.


## Version 1.2.0 

###  New Features 
- New Pro Feature: Customize text color, shadow, alignment, font style, and weight for individual buttons.
- New Feature: View blocked buttons within the deck. 
- New Pro Feature: You can block, unblock, and unblock all buttons using the context menu. 
- New Pro feature: Vibrate your mobile device with a SAMMI command
- These new features require the Deck Hopper Advanced extension to be installed. Download it at christinak.itch.io/deck-hopper.
- See setup guide on the website for more info on how to use these new features

### Improvements 
- When you click on an input field, it moves up so the keyboard doesn't hide it (Android)
- Improved visuals for active button overlay 
- Improved buttons and text inside some menus
- Improved banner scaling (Desktop)
- Tutorial: You will be prompted to review the updated sections whenever there are new feature introduced.

### Bug Fixes
- Fixed a problem where modifying a button's background to black didn't work
- Addressed a problem causing some actions to trigger multiple times, such as creating a new deck in SAMMI
- Fixed 'Always on top' not working for multiple windows. Now, the setting is applied per window (Desktop)
- Fixed brief banner flickering when launching Deck Hopper on the Pro version (Desktop)
- Addressed an issue with Pro version purchases not syncing correctly across platforms in some cases.


## Version 1.1.2

### Features & Improvements
- Added a beta Pro 'Vibrate' feature, available to Patrons
- Adjusted ads to not display immediately upon the first launch on mobile devices
- For Desktop version, a small ad has been added next to the 'Upgrade to Pro' banner, highlighting developer's extensions.
- Removed 'Classic Deck List' option as it wasn't being used

### Bug Fixes
- Fixed button modifications issues when navigating between decks where the button modifications wouldn't persist
    - Last modification takes priority, but button modification overrides group modification when switching decks or restarting Deck Hopper

### Known issues
- Some images with special characters in their filenames aren't shown correctly.
    - The easy fix is to rename the image. 

## Version 1.1.1

### Features & Improvements
- Added console logging to Settings - enable this before sending a bug report

### Known issues
- Some images with special characters in their filenames aren't shown correctly.
    - The easy fix is to rename the image. 

## Version 1.1.0

### Features & Improvements
- New Pro Feature: Add --deckId=YOURDECKID flag to the Deck Hopper shortcut to automatically enter a specific deck when Deck Hopper is launched, supports multiple decks open simultaneously
- New Free Feature: Transparent buttons now natively available in SAMMI Core 2024.1.0
- New Free Feature: Switch to Next and Previous deck via Deck App: Switch Deck command in SAMMI Core 2024.1.0
- New Free Feature: Always On Top for Deck Hopper Desktop (enable in Settings tab)
- Improved tutorial

### Bug Fixes
- 'Modify Button' command now correctly works for groups
- Fixed issues with recognizing Pro version in some cases
- Error handling improvements

### Known issues
- Some images with special characters in their filenames aren't shown correctly. This is a SAMMI Core bug. 
    - The easy fix is to rename the image. 

## Version 1.0.7
### Features & Improvements
- Clarified some text and error messages

### Known issues
- Some images with special characters in their filenames aren't shown correctly. This is a SAMMI Core bug. 
    - The easy fix is to rename the image. 

## Version 1.0.6

### Features & Improvements
- Improved text handling on extremely small buttons; text now shortens instead of disappearing.
- Enhanced text shadow for easier reading.
- Redesigned small button borders to be less round.
- Made the desktop basic version free, with an option to upgrade to Pro, similar to the mobile version.
    - Added a new banner and start-up prompt for desktop free users to upgrade, removed the annoying red strip and hourly pop-up alerts.
    - Since the desktop basic version is now free, all "Panel: Wait for" commands are exclusive to Pro.
- Clarified texts in forms.

### Known issues
- The 'Modify Button' command does not currently work for groups. This is a SAMMI Core bug. 
- Some images with special characters in their filenames aren't shown correctly. This is a SAMMI Core bug. 
    - The easy fix is to rename the image. 


## Version 1.0.5

### Features & Improvements
- Improved Webview detection, now including Chrome version checks. An option to dismiss warnings about outdated Webview is also available.
- Improved the sizing of the floating Hopper button, ensuring uniformity across various screen sizes.
- Updated the tutorial with instructions for deck switching, indicating that the "Switch Deck" button is obsolete. Users should use the "Panel: Switch Deck" command.
- Introduced more detailed error messages for better understanding.
- Provided clearer instructions for claiming the Pro version on different accounts/platforms after a mobile purchase.

## Version 1.0.4

### Features & Improvements
- Added patch notes screen whenever there's a new version available 
- Improvements for newly added supported SAMMI Core commands: Panel: Wait for Choice, Panel: Wait for Input, Panel: Wait for Multi Choice

### Bug Fixes

### Known issues
- Some images with special characters in their filenames aren't shown correctly. This is a SAMMI Core bug. The easy fix is to rename the image. 

## Version 1.0.3

### Features & Improvements
- Added a warning message when SAMMI Core is outdated and may not support all Deck Hoper features
- Added full support for the following SAMMI Core commands (available in SAMMI Core 2023.3.1^ and when Developer mode is enabled): 
    - Panel: Wait for Choice (allows user to select Yes or No)
    - Panel: Wait for Input (allows user to input text)
    - Panel: Wait for Multi Choice (allows user to select one of the options from the dropdown)

### Bug Fixes
- Fixed navigating between decks where it sometimes wouldn't register after you changed the deck order in SAMMI
- Other minor connection and login issues addressed

### Current Known Issues
Images with special characters in their filenames might not display properly due to a SAMMI Core bug. A quick fix is to rename the image.

## Version 1.0.2

### Features & Improvements
- Added a message prompting the installation or update of Webview if it is not installed or outdated on your device.

### Bug Fixes
- Fixed a bug where modified button image would not be removed if the default button did not have an image to begin with and SAMMI was reset

### Current Known Issues
Images with special characters in their filenames might not display properly due to a SAMMI Core bug. A quick fix is to rename the image.

## Version 1.0.1

### Features & Improvements
- Added Tutorial that will display to all new users
- Improvements for older devices with slow networks, such as more lenient timeouts when fetching deck and image data

### Current Known Issues
Images with special characters in their filenames might not display properly due to a SAMMI Core bug. A quick fix is to rename the image.

## Version 1.0.0.1

### Features & Improvements
- Deck Hopper now sends anonymous error reports (including details like stack trace, device info, app state) if it runs into unexpected errors. This helps fix bugs faster.
  - If you prefer not to send these reports, you can disable this in the Settings tab under 'Opt out of Error Reporting'.
- There's a new bug report feature in the Help tab. Please use this for bug reporting, as it sends useful extra information, making it much easier to solve issues.
- A 'Contact Support' button is added in the account tab for issues related to accounts or login. It links to a form on the website for assistance.
- We’ve brightened up the menu with some new button colors – just for fun!
- We’ve done a lot of code cleanup and optimization, aiming for a smoother experience.

### Bug Fixes:
- Resolved an issue where Deck Hopper wouldn't connect to SAMMI after several failed tries without a reload.
- Fixed an issue with button borders not applying correctly with the Modify button command.
- Solved a problem where buttons left a ghost border when their color was set to transparent and the image removed simultaneously.
- The setting to hide the floatingHopper button now saves correctly and persists between reloads.

### Current Known Issues
Images with special characters in their filenames might not display properly due to a SAMMI Core bug. A quick fix is to rename the image.

## Version 1.0.0

### Features & Improvements
- Added Account tab to the menu, users can now create a Deck Hopper account and log in (currently only used for purchase syncing across platforms)
- Added a way to purchase Deck Hopper Pro version to the Account tab
- Added icons to represent tabs in menu instead of text for smaller screens
- Added 'Ok' button to all tabs to prevent confusion about how to close the menu

### Known issues
- Some images with special characters in their filenames aren't shown correctly. This will need to be addressed in SAMMI Core.

## Version 1.2.2-beta

### Features & Improvements
- The settings and status bars have been resized.
- The settings window border has been removed for a cleaner appearance.
- Deck lists now show default images when there's no connection.
- The status bar now accurately shows if you're connected, disconnected, or if Deck Hopper is trying to reconnect.
- Instead of multiple pop-ups during reconnection, the status bar will now show a 'reconnecting' message, and only when you're in a deck will a pop-up appear saying Deck Hopper is attempting to reconnect.
- You can stop the reconnection attemps through the 'Stop reconnecting' button in the connection settings or via the pop-up.
- Greatly improved the speed of loading and saving button and deck images by caching them on your device.
- Implemented ads but they are currently not active.
- Launched a new website at christinak.ca/deckhopper
- The '?' menu has been redesigned to include website links, a troubleshooting guide, and a section for patch notes.
- Added a feature for strict image verification to ensure the latest images are used, which is helpful if you frequently change images in SAMMI but keep the same filename. It calculates and compares a new sum for updated images against the cached ones. Use this feature only if necessary as it could slow down image loading.

### Bug Fixes
- Fixed an issue where modifications to buttons wouldn't persist when changing screen orientation.

### Known issues
- Some images with special characters in their filenames aren't shown correctly. This will need to be addressed in SAMMI Core.


## Version 1.2.0-beta

### Features & Improvements: 
- Added a full deck list (can switch to the classic dropdown in Settings) with the ability to enable/disable decks, and show/hide disabled decks
- Added deck navigation using swipes
- Added support for transparent buttons
- Added haptic feedback to certain actions
- Reworked Settings menu
- Code optimizations  

### Bug Fixes:
- Hopper button remembers its position when switching decks
- You can tap away to close button/deck menu now  

### Note:
- Read ? in Settings for more info

## Version 1.1.1-beta
###  Features & Improvements:
- Updated icon
- Changed version formatting

### Bug Fixes:
- Fixed a typo in instructions about the default port being 9450 instead of 9470

## Version 1.1-beta
### Features & Improvements:
- Added 'Hide Hopper Button' to the settings menu
- Added quick Previous/Next deck navigation (activated by swiping up or down when Hopper icon is in edit mode)
- Updated ? section, including License and Privacy Policy links
- Code optimizations

### Bug Fixes:
- Fixed IP defaulting to 127.0.0.1 on mobile devices
- Fixed some decks not properly loading 

## Version 1.0-beta
- Initial beta release of Deck Hopper.
`;

export default patchNotes;